export const FIRST_REFERENCE_REQUIRED_FIELD_NAMES = [
  "basicReference1FirstName",
  "basicReference1LastName",
  "basicReference1PhoneNumber",
  "addressReference1AddressLine1",
  "addressReference1Landmark",
  "addressReference1Pincode",
  "addressReference1City",
  "addressReference1State",
  "addressReference1Country",
];

export const SECOND_REFERENCE_REQUIRED_FIELD_NAMES = [
  "basicReference2FirstName",
  "basicReference2LastName",
  "basicReference2PhoneNumber",
  "addressReference2AddressLine1",
  "addressReference2Landmark",
  "addressReference2Pincode",
  "addressReference2City",
  "addressReference2State",
  "addressReference2Country",
];

export const ALL_REFERENCE_REQUIRED_FIELD_NAMES = [
  ...FIRST_REFERENCE_REQUIRED_FIELD_NAMES,
  ...SECOND_REFERENCE_REQUIRED_FIELD_NAMES,
];

const normalizeOptionValue = (value: any) => {
  if (value && typeof value === "object") {
    return value.value ?? value.label ?? value.name ?? "";
  }
  return value;
};

const normalizeRuleValue = (value: any) =>
  String(normalizeOptionValue(value) ?? "")
    .trim()
    .toLowerCase();

const isNoValue = (value: any) => {
  const normalized = normalizeRuleValue(value);
  return normalized === "no" || normalized === "n" || normalized === "false";
};

const isOthersEmploymentType = (value: any) => {
  const normalized = normalizeRuleValue(value);
  return normalized === "other" || normalized === "others";
};

const isFarmerOrOthersEmploymentType = (value: any) =>
  normalizeRuleValue(value) === "farmer" || isOthersEmploymentType(value);

const isOtherRelationWithStudent = (value: any) => {
  const normalized = normalizeRuleValue(value);
  return normalized === "other" || normalized === "others";
};

const FIELD_METADATA_KEYS = new Set([
  "id",
  "label",
  "name",
  "type",
  "required",
  "options",
  "fieldOrder",
  "formFieldId",
  "formFieldKey",
  "formFieldValue",
  "isRemoved",
]);

const getFieldKey = (field: any): string => {
  if (!field || typeof field !== "object") return "";
  if (field.name) return String(field.name);
  if (field.formFieldKey) return String(field.formFieldKey);
  const dataEntry = Object.keys(field).find(
    (key) => !FIELD_METADATA_KEYS.has(key)
  );
  return dataEntry || "";
};

const getFieldValue = (field: any, values: Record<string, any> = {}) => {
  const fieldKey = getFieldKey(field);
  if (!fieldKey) return "";
  return (
    values?.[fieldKey] ??
    field?.value ??
    field?.formFieldValue ??
    field?.[fieldKey] ??
    ""
  );
};

export const isAdditionalDetailsValueFilled = (value: any): boolean => {
  const normalized = normalizeOptionValue(value);
  if (Array.isArray(normalized)) return normalized.length > 0;
  if (typeof normalized === "number") return true;
  if (typeof normalized === "boolean") return true;
  return String(normalized ?? "").trim() !== "";
};

export const buildFieldValueMap = (
  fields: any[] = [],
  values: Record<string, any> = {}
) => {
  const mapped: Record<string, any> = {};
  fields.forEach((field: any) => {
    const fieldKey = getFieldKey(field);
    if (fieldKey) {
      mapped[fieldKey] = getFieldValue(field, values);
    }

    if (field && typeof field === "object") {
      Object.entries(field).forEach(([key, value]) => {
        if (!FIELD_METADATA_KEYS.has(key)) {
          mapped[key] = values?.[key] ?? value;
        }
      });
    }
  });
  return { ...mapped, ...(values || {}) };
};

export const areNamedFieldsComplete = (
  fieldNames: string[],
  fields: any[] = [],
  values: Record<string, any> = {}
) => {
  const valueMap = buildFieldValueMap(fields, values);
  return fieldNames.every((fieldName) =>
    isAdditionalDetailsValueFilled(valueMap[fieldName])
  );
};

export const isFirstReferenceComplete = (
  _fields: any[] = [],
  _values: Record<string, any> = {},
  horizontalSteps: any[] = []
) => {
  const persistedFields = getReferenceCollateralDetailsFields(horizontalSteps);
  const persistedValues = buildFieldValueMap(persistedFields);
  return areNamedFieldsComplete(
    FIRST_REFERENCE_REQUIRED_FIELD_NAMES,
    persistedFields,
    persistedValues
  );
};

export const areRequiredFieldsComplete = (
  fields: any[] = [],
  values: Record<string, any> = {}
) => {
  const valueMap = buildFieldValueMap(fields, values);
  const requiredFields = (fields || []).filter(
    (field: any) => field?.required !== false && getFieldKey(field)
  );
  if (requiredFields.length === 0) return false;
  return requiredFields.every((field: any) => {
    const fieldKey = getFieldKey(field);
    return isAdditionalDetailsValueFilled(valueMap[fieldKey]);
  });
};

const findVerticalStep = (verticalSteps: any[] = [], stepName: string) =>
  (verticalSteps || []).find(
    (step: any) =>
      String(step?.verticalStepName || step?.stepLabel || "").trim() ===
      stepName
  );

const findHorizontalStep = (
  horizontalSteps: any[] = [],
  stepName: string,
  stepId?: number
) =>
  (horizontalSteps || []).find((step: any) => {
    const currentStepName = String(
      step?.horizontalStepName || step?.stepLabel || ""
    ).trim();
    return currentStepName === stepName || Number(step?.horizontalStepId) === stepId;
  });

export const getReferenceCollateralDetailsFields = (
  horizontalSteps: any[] = []
) => {
  const additionalDetailsStep = horizontalSteps.find(
    (step: any) => Number(step?.horizontalStepId ?? step?.id) === 4
  );
  const referenceStep = additionalDetailsStep?.verticalSteps?.find(
    (step: any) =>
      String(step?.verticalStepName || step?.stepLabel || "").trim() ===
      "Reference Details"
  );
  const referenceCollateralStep =
    referenceStep ||
    additionalDetailsStep?.verticalSteps?.find(
      (step: any) =>
        String(step?.verticalStepName || step?.stepLabel || "").trim() ===
          "Reference & Collateral Details" ||
        Number(step?.verticalStepId ?? step?.id) === 13
  );
  return referenceCollateralStep?.fields || [];
};

export const isAdditionalDetailsSectionEditBlocked = (
  verticalSteps: any[] = [],
  targetStepName: string,
  horizontalSteps: any[] = []
) => {
  if (targetStepName === "Applicant Additional Details") {
    const coApplicantFields =
      horizontalSteps
        .find((step: any) => Number(step?.horizontalStepId) === 2)
        ?.verticalSteps?.find(
          (step: any) => Number(step?.verticalStepId) === 9
        )?.fields || [];

    return !isCoApplicantOneAdditionalDetailsComplete(coApplicantFields);
  }

  if (targetStepName === "Funds & Expenses") {
    const applicantAdditionalStep = findVerticalStep(
      verticalSteps,
      "Applicant Additional Details"
    );
    return !areRequiredFieldsComplete(applicantAdditionalStep?.fields || []);
  }

  if (
    targetStepName === "Reference & Collateral Details" ||
    targetStepName === "Reference Details"
  ) {
    const fundsStep = findVerticalStep(verticalSteps, "Funds & Expenses");
    return !areRequiredFieldsComplete(fundsStep?.fields || []);
  }

  if (targetStepName === "Collateral Details") {
    const referenceStep = findVerticalStep(verticalSteps, "Reference Details");
    const referenceFields =
      referenceStep?.fields?.length > 0
        ? referenceStep.fields
        : getReferenceCollateralDetailsFields(horizontalSteps);
    return !areNamedFieldsComplete(
      ALL_REFERENCE_REQUIRED_FIELD_NAMES,
      referenceFields
    );
  }

  return false;
};

export const isCoApplicantEmploymentFieldVisibleForAdditionalDetails = (
  fieldName: string,
  values: Record<string, any>,
  coApplicantNumber: string | number
) => {
  const isEmployed = values?.[`employmentCoApplicant${coApplicantNumber}isEmployed`];
  const employmentType =
    values?.[`employmentCoApplicant${coApplicantNumber}employmentType`];

  if (!/^employmentCoApplicant\d+/i.test(fieldName)) return true;
  if (/isEmployed$/i.test(fieldName)) return true;
  if (!isAdditionalDetailsValueFilled(isEmployed)) return false;
  if (isNoValue(isEmployed)) {
    return false;
  }
  if (/employmentType$/i.test(fieldName)) return true;
  if (!isAdditionalDetailsValueFilled(employmentType)) return false;
  if (isOthersEmploymentType(employmentType)) {
    return /monthlyIncome$|monthlyObligation$/i.test(fieldName);
  }
  return true;
};

export const isCoApplicantFieldRequiredForAdditionalDetails = (
  field: any,
  values: Record<string, any>,
  coApplicantNumber: string | number
) => {
  const fieldName = getFieldKey(field);
  if (!fieldName) return false;

  if (/^basicCoApplicant\d+OtherRelationWithStudent$/i.test(fieldName)) {
    const relationWithStudent =
      values?.[`basicCoApplicant${coApplicantNumber}RelationWithStudent`];
    return (
      field?.required !== false &&
      isOtherRelationWithStudent(relationWithStudent)
    );
  }

  if (
    !isCoApplicantEmploymentFieldVisibleForAdditionalDetails(
      fieldName,
      values,
      coApplicantNumber
    )
  ) {
    return false;
  }

  if (/^employmentCoApplicant\d+/i.test(fieldName)) {
    if (/isEmployed$|employmentType$/i.test(fieldName)) return true;
    const employmentType =
      values?.[`employmentCoApplicant${coApplicantNumber}employmentType`];
    if (isOthersEmploymentType(employmentType)) {
      return /monthlyIncome$|monthlyObligation$/i.test(fieldName);
    }
  }

  return field?.required !== false;
};

export const isCoApplicantGroupCompleteForAdditionalDetails = (
  group: { basic?: any[]; address?: any[]; employment?: any[] },
  values: Record<string, any>,
  coApplicantNumber: string | number
) => {
  const allFields = [
    ...(group?.basic || []),
    ...(group?.address || []),
    ...(group?.employment || []),
  ];
  const valueMap = buildFieldValueMap(allFields, values);
  const requiredFields = allFields.filter((field: any) =>
    isCoApplicantFieldRequiredForAdditionalDetails(
      field,
      valueMap,
      coApplicantNumber
    )
  );

  if (requiredFields.length === 0) return false;
  return requiredFields.every((field: any) => {
    const fieldKey = getFieldKey(field);
    return isAdditionalDetailsValueFilled(valueMap[fieldKey]);
  });
};

const getCoApplicantFieldGroups = (fields: any[] = []) => {
  const groups: Record<
    string,
    { basic: any[]; address: any[]; employment: any[] }
  > = {};

  fields.forEach((field: any) => {
    const fieldKey = getFieldKey(field);
    const match = fieldKey.match(/^(basic|address|employment)CoApplicant(\d+)/i);
    if (!match) return;

    const section = match[1].toLowerCase() as "basic" | "address" | "employment";
    const coApplicantKey = `CoApplicant${match[2]}`;
    if (!groups[coApplicantKey]) {
      groups[coApplicantKey] = { basic: [], address: [], employment: [] };
    }
    groups[coApplicantKey][section].push({
      ...field,
      name: field?.name || fieldKey,
    });
  });

  return groups;
};

const shouldSkipCoApplicantDependencyField = (fieldName: string) =>
  /^basicCoApplicant\d+Aadhaar(Number|Verified)$/i.test(fieldName) ||
  /^addressCoApplicant\d+(permanentAddressLine[23]|currentAddressLine[23])$/i.test(fieldName) ||
  /^addressCoApplicant\d+SameAs$/i.test(fieldName);

const isCoApplicantOneAdditionalDetailsComplete = (fields: any[] = []) => {
  const groups = getCoApplicantFieldGroups(fields);
  const coApplicantOne = groups.CoApplicant1;
  if (
    !coApplicantOne?.basic?.length ||
    !coApplicantOne?.address?.length ||
    !coApplicantOne?.employment?.length
  ) {
    return false;
  }

  const values = buildFieldValueMap(fields);
  const relationWithStudent =
    values.basicCoApplicant1RelationWithStudent;
  const isEmployed = values.employmentCoApplicant1isEmployed;
  const employmentType = values.employmentCoApplicant1employmentType;

  const applicableFields = [
    ...coApplicantOne.basic.filter((field: any) => {
      const fieldName = getFieldKey(field);
      if (shouldSkipCoApplicantDependencyField(fieldName)) return false;
      if (/^basicCoApplicant\d+OtherRelationWithStudent$/i.test(fieldName)) {
        return isOtherRelationWithStudent(relationWithStudent);
      }
      return true;
    }),
    ...coApplicantOne.address.filter((field: any) => {
      const fieldName = getFieldKey(field);
      return !shouldSkipCoApplicantDependencyField(fieldName);
    }),
    ...coApplicantOne.employment.filter((field: any) => {
      const fieldName = getFieldKey(field);
      if (/isEmployed$/i.test(fieldName)) return true;
      if (isNoValue(isEmployed)) return false;
      if (/employmentType$/i.test(fieldName)) return true;
      return !isFarmerOrOthersEmploymentType(employmentType);
    }),
  ];

  return applicableFields.every((field: any) =>
    isAdditionalDetailsValueFilled(values[getFieldKey(field)])
  );
};
